/**
 * Dexie ↔ Firebase Incremental Sync
 *
 * Two-way sync between local Dexie database and Firebase Firestore.
 *
 * Conflict resolution:
 * - Program selection: commutative merge (mergeProgramSelection).
 * - 1RMs: take max weight per exercise (weight only goes up).
 * - Progression state: union for reward arrays; max for intel scalars.
 * - Sessions, recovery, daily activity, nutrition, custom meals:
 *     On download, preserve any local entry that is `local`/`pending`
 *     AND has a newer updatedAt than the remote copy. This prevents
 *     silent loss of unsynced edits when two devices modify the same
 *     record and the other device's write lands first. Local pending
 *     edits are pushed on the next upload cycle, where Firestore's
 *     `{ merge: true }` sets completes the field-level merge.
 * - Background sync queue for reliability.
 */

import { db, DBSession, DBRecoveryEntry, DB1RMEntry, DBDailyActivity, markSynced } from './db';
import type { NutritionEntry, CustomMeal } from './types/nutrition';
import {
  mergeProgramSelection,
  programSelectionEquals,
  filterPreserveLocalPending,
  type ProgramSelectionData,
} from './syncMerge';
export { mergeProgramSelection, filterPreserveLocalPending } from './syncMerge';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  query,
  where,
  getDocs,
  serverTimestamp,
  Timestamp,
  writeBatch,
  limit as firestoreLimit,
  orderBy,
  arrayUnion,
  updateDoc,
  deleteField,
} from 'firebase/firestore';
import { db as firestore } from './firebase';

// ============================================================================
// UTILITIES
// ============================================================================

/**
 * Coerce anything that might represent a date into a real Date, or
 * undefined if it can't. Handles all the shapes we've actually seen come
 * back from Firestore: native Timestamp (`.toDate()`), already-converted
 * Date instances, ISO strings, and numeric epoch ms. Guards every call
 * site against `TypeError: x.toDate is not a function` from non-Timestamp
 * values that snuck through (e.g. legacy direct writes that bypassed the
 * Firebase SDK's serializer).
 */
function toDateSafe(val: any): Date | undefined {
  if (!val) return undefined;
  if (val.toDate && typeof val.toDate === 'function') return val.toDate(); // Firestore Timestamp
  if (val instanceof Date) return val;
  if (typeof val === 'string' || typeof val === 'number') {
    const d = new Date(val);
    return isNaN(d.getTime()) ? undefined : d;
  }
  return undefined;
}

/**
 * Sanitize data for Firestore (remove undefined values)
 * Firestore doesn't support undefined - must be null or omitted
 */
function sanitizeForFirestore(obj: any): any {
  if (obj === null || obj === undefined) {
    return null;
  }
  
  if (obj instanceof Date || obj instanceof Timestamp) {
    return obj;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(sanitizeForFirestore);
  }
  
  if (typeof obj === 'object') {
    const sanitized: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        sanitized[key] = sanitizeForFirestore(value);
      }
    }
    return sanitized;
  }
  
  return obj;
}

/**
 * Wrapper for Dexie bulkPut that:
 *  - Paginates large arrays into 500-item chunks. A single bulkPut of
 *    thousands of records can blow IndexedDB quota or hit transaction
 *    timeouts on lower-end mobile browsers. Chunking keeps each transaction
 *    small enough to commit reliably.
 *  - Gracefully handles quota errors. When IndexedDB is full, the data
 *    still exists on Firebase — log and continue rather than crashing
 *    the sync.
 */
const BULK_PUT_CHUNK_SIZE = 500;

async function safeBulkPut<T>(
  table: { bulkPut: (items: T[]) => Promise<any> },
  items: T[],
  label: string,
): Promise<void> {
  if (items.length === 0) return;

  // Single small batch — fast path, no chunking overhead.
  if (items.length <= BULK_PUT_CHUNK_SIZE) {
    return safeBulkPutChunk(table, items, label);
  }

  // Large batch — paginate so a single quota/timeout failure doesn't lose
  // the whole download.
  const totalChunks = Math.ceil(items.length / BULK_PUT_CHUNK_SIZE);
  let written = 0;
  let quotaHit = false;

  for (let i = 0; i < items.length; i += BULK_PUT_CHUNK_SIZE) {
    const chunk = items.slice(i, i + BULK_PUT_CHUNK_SIZE);
    try {
      await table.bulkPut(chunk);
      written += chunk.length;
    } catch (error: any) {
      if (error?.name === 'QuotaExceededError' || error?.message?.includes('quota')) {
        console.error(
          `[sync] Storage quota exceeded mid-write of ${label} ` +
          `(chunk ${Math.floor(i / BULK_PUT_CHUNK_SIZE) + 1}/${totalChunks}, ` +
          `${written}/${items.length} written). Data is safe on Firebase.`,
        );
        quotaHit = true;
        break; // No point trying more chunks if we're out of space.
      }
      throw error;
    }
  }

  if (!quotaHit && totalChunks > 1) {
    console.log(`[sync] safeBulkPut(${label}): wrote ${written} items in ${totalChunks} chunks`);
  }
}

async function safeBulkPutChunk<T>(
  table: { bulkPut: (items: T[]) => Promise<any> },
  items: T[],
  label: string,
): Promise<void> {
  try {
    await table.bulkPut(items);
  } catch (error: any) {
    if (error?.name === 'QuotaExceededError' || error?.message?.includes('quota')) {
      console.error(`[sync] Storage quota exceeded while writing ${label} (${items.length} items). Data exists on Firebase — skipping local write.`);
      return;
    }
    throw error;
  }
}


// ============================================================================
// PROGRAM SELECTION SYNC
// ============================================================================
// The pure `mergeProgramSelection` / `programSelectionEquals` helpers live in
// ./syncMerge so they can be unit-tested without booting Firebase or Dexie.

/**
 * Perform a program selection merge between Dexie and Firestore.
 * Reads both sides, merges, and writes back only if changed.
 */
/**
 * Backfill the training baseline (bodyweight / age / gender / experience) from
 * Firestore into Dexie.
 *
 * Onboarding has always written these to Firestore only, and the load engine
 * reads Dexie — so `bodyWeightForLevel` was undefined for EVERY user and
 * detectTrainingLevel's relative-strength branch never ran. Onboarding now
 * writes both, but every EXISTING user already finished onboarding and will
 * never run it again; without this backfill they'd stay stuck on the beginner
 * floor forever.
 *
 * Firestore is treated as the source of truth here because it is the only place
 * these values have ever been written. Runs on every sync and is a no-op once
 * Dexie already agrees.
 */
async function syncTrainingBaseline(userId: string): Promise<void> {
  try {
    const userSnap = await getDoc(doc(firestore, 'users', userId));
    if (!userSnap.exists()) return;
    const d = userSnap.data() ?? {};

    // Onboarding stores bodyweight as `weight`; tolerate either key.
    const remote = {
      bodyWeight: typeof d.bodyWeight === 'number' ? d.bodyWeight
        : (typeof d.weight === 'number' ? d.weight : undefined),
      age: typeof d.age === 'number' ? d.age : undefined,
      gender: (d.gender === 'male' || d.gender === 'female') ? d.gender : undefined,
      experienceLevel: ['beginner', 'intermediate', 'advanced', 'elite'].includes(d.experienceLevel)
        ? d.experienceLevel : undefined,
    };
    if (Object.values(remote).every(v => v === undefined)) return;

    const local = await db.userProfiles.get(userId);
    const differs = (Object.keys(remote) as (keyof typeof remote)[])
      .some(k => remote[k] !== undefined && (local as any)?.[k] !== remote[k]);
    if (!differs) return;

    const { saveTrainingBaseline } = await import('./db');
    await saveTrainingBaseline(userId, remote as any);
    console.log('[sync] Training baseline backfilled into Dexie:', remote);
  } catch (e) {
    console.warn('[sync] Training baseline backfill failed (non-critical):', e);
  }
}

async function syncProgramSelection(userId: string): Promise<void> {
  try {
    // Read remote from Firestore
    const userRef = doc(firestore, 'users', userId);
    const userSnap = await getDoc(userRef);
    const remoteData = userSnap.exists() ? userSnap.data()?.programSelection : null;

    let remoteProgramSelection: ProgramSelectionData | null = null;
    if (remoteData) {
      const startDate = remoteData.startDate?.toDate
        ? remoteData.startDate.toDate().toISOString()
        : (typeof remoteData.startDate === 'string' ? remoteData.startDate : new Date(remoteData.startDate).toISOString());
      remoteProgramSelection = {
        programId: remoteData.programId,
        programLevel: remoteData.programLevel,
        startDate,
        currentWeek: remoteData.currentWeek,
        currentDay: remoteData.currentDay,
        completedDays: remoteData.completedDays || [],
        skippedDays: remoteData.skippedDays || [],
        syncVersion: remoteData.syncVersion || 0,
        updatedAt: remoteData.updatedAt || '',
        ...(remoteData.restartedAt ? { restartedAt: remoteData.restartedAt } : {}),
        // This projection runs the SAME merge as saveUserProgramSelection. If
        // it drops the intent stamp and the un-skip tombstones, the next sync
        // tick re-applies never-regress and re-unions the skip list, undoing
        // the write that just succeeded.
        ...(remoteData.unskippedDays?.length ? { unskippedDays: remoteData.unskippedDays } : {}),
        ...(remoteData.positionSetAt ? { positionSetAt: remoteData.positionSetAt } : {}),
        ...(remoteData.positionWeek != null ? { positionWeek: remoteData.positionWeek } : {}),
        ...(remoteData.positionDay != null ? { positionDay: remoteData.positionDay } : {}),
      };
    }

    // Read local from Dexie
    const localProfile = await db.userProfiles.get(userId);
    const localProgramSelection: ProgramSelectionData | null = localProfile?.programSelection
      ? {
          programId: localProfile.programSelection.programId,
          programLevel: localProfile.programSelection.programLevel,
          startDate: localProfile.programSelection.startDate,
          currentWeek: localProfile.programSelection.currentWeek,
          currentDay: localProfile.programSelection.currentDay,
          completedDays: localProfile.programSelection.completedDays || [],
          skippedDays: localProfile.programSelection.skippedDays || [],
          syncVersion: localProfile.programSelection.syncVersion || 0,
          updatedAt: localProfile.programSelection.updatedAt || '',
          ...((localProfile.programSelection as any).restartedAt ? { restartedAt: (localProfile.programSelection as any).restartedAt } : {}),
          ...((localProfile.programSelection as any).unskippedDays?.length
            ? { unskippedDays: (localProfile.programSelection as any).unskippedDays }
            : {}),
          ...((localProfile.programSelection as any).positionSetAt
            ? { positionSetAt: (localProfile.programSelection as any).positionSetAt }
            : {}),
          ...((localProfile.programSelection as any).positionWeek != null
            ? { positionWeek: (localProfile.programSelection as any).positionWeek }
            : {}),
          ...((localProfile.programSelection as any).positionDay != null
            ? { positionDay: (localProfile.programSelection as any).positionDay }
            : {}),
        }
      : null;

    // Merge
    const merged = mergeProgramSelection(localProgramSelection, remoteProgramSelection);
    if (!merged) return;

    // Write to Dexie if changed from local
    if (!programSelectionEquals(merged, localProgramSelection)) {
      console.log('[sync] Program selection merged (local updated):', {
        localWeek: localProgramSelection?.currentWeek, localDay: localProgramSelection?.currentDay,
        remoteWeek: remoteProgramSelection?.currentWeek, remoteDay: remoteProgramSelection?.currentDay,
        mergedWeek: merged.currentWeek, mergedDay: merged.currentDay,
      });
      await db.userProfiles.update(userId, {
        programSelection: merged,
        updatedAt: new Date(),
      });
      // If user profile doesn't exist yet, fall back to put
      const check = await db.userProfiles.get(userId);
      if (!check) {
        await db.userProfiles.put({
          userId,
          programSelection: merged,
          updatedAt: new Date(),
        } as any);
      }
    }

    // Write to Firestore if changed from remote
    if (!programSelectionEquals(merged, remoteProgramSelection)) {
      console.log('[sync] Program selection merged (remote updated)');
      const firestoreSelection = sanitizeForFirestore({
        ...merged,
        startDate: Timestamp.fromDate(new Date(merged.startDate)),
      });
      await setDoc(userRef, { programSelection: firestoreSelection }, { merge: true });
    }
  } catch (error) {
    console.warn('[sync] Program selection merge failed (non-critical):', error);
  }
}

// ============================================================================
// TYPES
// ============================================================================

export interface SyncStats {
  uploaded: {
    sessions: number;
    recovery: number;
    oneRMs: number;
    progressionStates: number;
    dailyActivity: number;
    nutrition: number;
    customMeals: number;
  };
  downloaded: {
    sessions: number;
    recovery: number;
    oneRMs: number;
    progressionStates: number;
    dailyActivity: number;
    nutrition: number;
    customMeals: number;
  };
  errors: string[];
}

export interface SyncOptions {
  direction?: 'up' | 'down' | 'both';
  force?: boolean; // Force sync even if not needed
  batchSize?: number; // Max items per batch (default: 50)
}

// ============================================================================
// UPLOAD: Dexie → Firebase
// ============================================================================

/**
 * Upload all pending local changes to Firebase
 */
export async function uploadPendingChanges(
  userId: string, 
  options: SyncOptions = {}
): Promise<SyncStats> {
  const { batchSize = 50 } = options;
  const stats: SyncStats = {
    uploaded: { sessions: 0, recovery: 0, oneRMs: 0, progressionStates: 0, dailyActivity: 0, nutrition: 0, customMeals: 0 },
    downloaded: { sessions: 0, recovery: 0, oneRMs: 0, progressionStates: 0, dailyActivity: 0, nutrition: 0, customMeals: 0 },
    errors: []
  };

  console.log('~ Starting upload of pending changes...');

  try {
    // Get all items marked as 'local' or 'pending'.
    // Filter progression states to the current user — if a prior account's
    // record lingers on a shared device, including it would attempt an
    // upload under the new user's path and fail the rule's userId match.
    const [localSessions, localRecovery, localOneRMs, allProgressionStates, localDailyActivity, localNutrition, localCustomMeals] = await Promise.all([
      db.sessions.where('syncStatus').anyOf(['local', 'pending']).toArray(),
      db.recovery.where('syncStatus').anyOf(['local', 'pending']).toArray(),
      db.oneRMs.where('syncStatus').anyOf(['local', 'pending']).toArray(),
      db.progressionStates.where('syncStatus').anyOf(['local', 'pending']).toArray(),
      db.dailyActivity.where('syncStatus').anyOf(['local', 'pending']).toArray(),
      db.nutritionEntries.where('syncStatus').anyOf(['local', 'pending']).toArray(),
      db.customMeals.where('syncStatus').anyOf(['local', 'pending']).toArray(),
    ]);
    const localProgressionStates = allProgressionStates.filter(p => !p.userId || p.userId === userId);

    console.log(` Found ${localSessions.length} sessions, ${localRecovery.length} recovery, ${localOneRMs.length} 1RMs, ${localProgressionStates.length} progression states, ${localDailyActivity.length} activity, ${localNutrition.length} nutrition, ${localCustomMeals.length} custom meals to sync`);

    // Upload sessions in batches
    if (localSessions.length > 0) {
      const sessionBatches = chunkArray(localSessions, batchSize);
      for (const batch of sessionBatches) {
        try {
          await uploadSessionBatch(userId, batch);
          stats.uploaded.sessions += batch.length;
        } catch (error) {
          const msg = `Failed to upload session batch: ${error}`;
          console.error(msg);
          stats.errors.push(msg);
        }
      }
    }

    // Upload recovery in batches
    if (localRecovery.length > 0) {
      const recoveryBatches = chunkArray(localRecovery, batchSize);
      for (const batch of recoveryBatches) {
        try {
          await uploadRecoveryBatch(userId, batch);
          stats.uploaded.recovery += batch.length;
        } catch (error) {
          const msg = `Failed to upload recovery batch: ${error}`;
          console.error(msg);
          stats.errors.push(msg);
        }
      }
    }

    // Upload 1RMs in batches
    if (localOneRMs.length > 0) {
      const oneRMBatches = chunkArray(localOneRMs, batchSize);
      for (const batch of oneRMBatches) {
        try {
          await upload1RMBatch(userId, batch);
          stats.uploaded.oneRMs += batch.length;
        } catch (error) {
          const msg = `Failed to upload 1RM batch: ${error}`;
          console.error(msg);
          stats.errors.push(msg);
        }
      }
    }

    // Upload progression states
    if (localProgressionStates.length > 0) {
      try {
        await uploadProgressionState(userId, localProgressionStates[0]); // Only one per user
        stats.uploaded.progressionStates += 1;
      } catch (error) {
        const msg = `Failed to upload progression state: ${error}`;
        console.error(msg);
        stats.errors.push(msg);
      }
    }

    // Upload daily activity in batches
    if (localDailyActivity.length > 0) {
      const activityBatches = chunkArray(localDailyActivity, batchSize);
      for (const batch of activityBatches) {
        try {
          await uploadDailyActivityBatch(userId, batch);
          stats.uploaded.dailyActivity += batch.length;
        } catch (error) {
          const msg = `Failed to upload daily activity batch: ${error}`;
          console.error(msg);
          stats.errors.push(msg);
        }
      }
    }

    // Upload nutrition entries in batches
    if (localNutrition.length > 0) {
      const nutritionBatches = chunkArray(localNutrition, batchSize);
      for (const batch of nutritionBatches) {
        try {
          await uploadNutritionBatch(userId, batch);
          stats.uploaded.nutrition += batch.length;
        } catch (error) {
          const msg = `Failed to upload nutrition batch: ${error}`;
          console.error(msg);
          stats.errors.push(msg);
        }
      }
    }

    // Upload custom meals in batches
    if (localCustomMeals.length > 0) {
      const mealBatches = chunkArray(localCustomMeals, batchSize);
      for (const batch of mealBatches) {
        try {
          await uploadCustomMealsBatch(userId, batch);
          stats.uploaded.customMeals += batch.length;
        } catch (error) {
          const msg = `Failed to upload custom meals batch: ${error}`;
          console.error(msg);
          stats.errors.push(msg);
        }
      }
    }

    // Upload spearhead progress
    try {
      await uploadSpearheadProgress(userId);
    } catch (error) {
      const msg = `Failed to upload spearhead progress: ${error}`;
      console.error(msg);
      stats.errors.push(msg);
    }

    // Merge program selection before upload completes (Step 3)
    try {
      await syncProgramSelection(userId);
    } catch (error) {
      console.warn('Program selection merge during upload failed (non-critical):', error);
    }

    console.log(' Upload complete:', stats.uploaded);
  } catch (error) {
    const msg = `Upload failed: ${error}`;
    console.error(msg);
    stats.errors.push(msg);
  }

  return stats;
}

/**
 * Upload a batch of sessions to Firebase
 *
 * Idempotent: Uses session.id as the Firestore document ID with `set(..., { merge: true })`,
 * so re-uploads (e.g., if marking as synced in Dexie fails) overwrite rather than duplicate.
 */
/** A Date we can actually hand to Firestore, or null. */
function asValidDate(v: unknown): Date | null {
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  if (typeof v === 'string' || typeof v === 'number') {
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }
  // Firestore Timestamp round-tripped back into Dexie.
  if (v && typeof (v as any).toDate === 'function') {
    try {
      const d = (v as any).toDate();
      return d instanceof Date && !isNaN(d.getTime()) ? d : null;
    } catch { return null; }
  }
  return null;
}

/**
 * Session IDs are minted as `session_${Date.now()}_${rand}`, so a corrupt date
 * field can usually be recovered from the id itself rather than invented.
 */
function dateFromSessionId(id: string): Date | null {
  const m = /^session_(\d{10,})_/.exec(id);
  if (!m) return null;
  const d = new Date(Number(m[1]));
  return isNaN(d.getTime()) ? null : d;
}

/** First usable date from the candidates, else null. */
function firstValidDate(...candidates: unknown[]): Date | null {
  for (const c of candidates) {
    const d = asValidDate(c);
    if (d) return d;
  }
  return null;
}

async function uploadSessionBatch(userId: string, sessions: DBSession[]): Promise<void> {
  const batch = writeBatch(firestore);
  const now = new Date();

  // Sessions that could not be prepared. They are skipped rather than allowed
  // to throw, because this is a BATCH: `Timestamp.fromDate` throws
  // "RangeError: Invalid time value" on an invalid Date, and one such session
  // aborted the upload of up to 50 others — permanently, since every retry hit
  // the same bad record. A user's whole training history could sit unsynced
  // behind a single corrupt timestamp, with the only symptom a console error.
  const skipped: string[] = [];
  let queued = 0;
  const queuedIds: string[] = [];

  for (const session of sessions) {
    try {
      // Repair rather than discard where we can: the id encodes the creation
      // epoch, so a session with a corrupt `date` is still placeable in time.
      const idDate = dateFromSessionId(session.id);
      const date = firstValidDate(session.date, session.createdAt, session.startTime, idDate);
      if (!date) {
        // No usable date anywhere. Uploading with a fabricated one would put a
        // phantom workout in the user's history, which is worse than skipping.
        skipped.push(session.id);
        continue;
      }
      const startTime = firstValidDate(session.startTime, session.date, idDate) ?? date;
      const createdAt = firstValidDate(session.createdAt, idDate, session.date) ?? date;
      const endTime = firstValidDate(session.endTime);

      // Document ID = session.id ensures idempotent upserts (no duplicates on retry)
      const sessionRef = doc(firestore, `users/${userId}/sessions`, session.id);

      const firestoreData = sanitizeForFirestore({
        ...session,
        userId,
        date: Timestamp.fromDate(date),
        startTime: Timestamp.fromDate(startTime),
        endTime: endTime ? Timestamp.fromDate(endTime) : null,
        createdAt: Timestamp.fromDate(createdAt),
        updatedAt: serverTimestamp(),
        syncedAt: serverTimestamp(),
      });

      batch.set(sessionRef, firestoreData, { merge: true });
      queued++;
      queuedIds.push(session.id);
    } catch (e) {
      // Anything unexpected in ONE session must not take the batch down.
      console.warn(`[sync] Skipping session ${session.id} — could not prepare for upload:`, e);
      skipped.push(session.id);
    }
  }

  if (skipped.length > 0) {
    console.warn(
      `[sync] ${skipped.length} session(s) skipped as unpreparable (no recoverable date): ${skipped.join(', ')}`
    );
  }

  if (queued === 0) {
    // Nothing to commit. Returning normally (rather than throwing) stops the
    // caller from logging a batch failure forever for records that can never
    // succeed.
    return;
  }

  await batch.commit();

  // Mark ONLY what we actually uploaded. Marking the skipped ones synced would
  // silently orphan them.
  await markSynced('sessions', queuedIds);
  console.log(` Uploaded ${queued} sessions${skipped.length ? ` (${skipped.length} skipped)` : ''}`);
}

/**
 * Upload a batch of recovery entries to Firebase
 */
async function uploadRecoveryBatch(userId: string, entries: DBRecoveryEntry[]): Promise<void> {
  const batch = writeBatch(firestore);

  // Same all-or-nothing hazard as uploadSessionBatch: one invalid Date makes
  // Timestamp.fromDate throw and takes the whole batch with it, forever.
  const queuedIds: string[] = [];
  const skipped: string[] = [];

  for (const entry of entries) {
    const date = firstValidDate(entry.date, entry.createdAt);
    const createdAt = firstValidDate(entry.createdAt, entry.date);
    if (!date || !createdAt) {
      skipped.push(entry.id);
      continue;
    }
    const recoveryRef = doc(firestore, `users/${userId}/recovery`, entry.id);

    const firestoreData = sanitizeForFirestore({
      ...entry,
      date: Timestamp.fromDate(date),
      createdAt: Timestamp.fromDate(createdAt),
      updatedAt: serverTimestamp(),
      syncedAt: serverTimestamp(),
    });

    batch.set(recoveryRef, firestoreData, { merge: true });
    queuedIds.push(entry.id);
  }

  if (skipped.length > 0) {
    console.warn(`[sync] ${skipped.length} recovery entr(ies) skipped — unusable date: ${skipped.join(', ')}`);
  }
  if (queuedIds.length === 0) return;

  await batch.commit();

  // Mark as synced in Dexie
  await markSynced('recovery', queuedIds);
  console.log(` Uploaded ${queuedIds.length} recovery entries${skipped.length ? ` (${skipped.length} skipped)` : ''}`);
}

/**
 * Upload a batch of 1RMs to Firebase
 */
async function upload1RMBatch(userId: string, oneRMs: DB1RMEntry[]): Promise<void> {
  // 1RMs are stored in the user document under the 'oneRMs' field
  const userRef = doc(firestore, 'users', userId);
  
  // Convert to key-value map
  const oneRMMap: Record<string, any> = {};
  const queuedIds: string[] = [];
  const skipped: string[] = [];
  for (const entry of oneRMs) {
    // An invalid date here threw and blocked EVERY 1RM, not just this one.
    const date = firstValidDate(entry.date);
    if (!date) {
      skipped.push(entry.id);
      continue;
    }
    oneRMMap[entry.exerciseName] = {
      weight: entry.weight,
      date: Timestamp.fromDate(date),
      source: entry.source,
      confidence: entry.confidence,
      updatedAt: serverTimestamp(),
    };
    queuedIds.push(entry.id);
  }

  if (skipped.length > 0) {
    console.warn(`[sync] ${skipped.length} 1RM(s) skipped — unusable date: ${skipped.join(', ')}`);
  }
  if (queuedIds.length === 0) return;

  await setDoc(userRef, { oneRMs: oneRMMap }, { merge: true });

  // Mark as synced in Dexie
  await markSynced('oneRMs', queuedIds);
  console.log(` Uploaded ${queuedIds.length} 1RMs${skipped.length ? ` (${skipped.length} skipped)` : ''}`);
}

/**
 * Upload progression state to Firebase
 */
async function uploadProgressionState(userId: string, state: any): Promise<void> {
  // Skip if the local record belongs to a different user — happens on
  // shared devices when a prior account's Dexie data lingers. The
  // progression doc rule's create check is `data.userId == path.userId`
  // so a mismatched record would fail with permission-denied.
  if (state?.userId && state.userId !== userId) {
    console.warn(` Skipping progression upload — record userId (${state.userId}) doesn't match current user (${userId})`);
    return;
  }

  const progressionRef = doc(firestore, `users/${userId}/progression`, 'current');

  // Use arrayUnion for cosmetic/reward arrays to prevent overwriting server-side
  // additions (e.g. operation rewards added directly to Firestore)
  const arrayFields = [
    'unlockedEmblems', 'unlockedTitles', 'unlockedCallingCards',
    'unlockedAvatars', 'unlockedArchetypes', 'milestonesUnlocked',
    'operatorScoreMilestonesUnlocked', 'awardedSessionIds',
  ];

  // Build the base data without array fields
  const { ...baseState } = state;
  for (const field of arrayFields) {
    delete baseState[field];
  }

  const firestoreData = sanitizeForFirestore({
    ...baseState,
    // Force userId to match the current auth user. Without this, a
    // stale or missing state.userId would fail the create rule check
    // `request.resource.data.userId == userId` on first sync.
    userId,
    updatedAt: serverTimestamp(),
    syncedAt: serverTimestamp(),
  });

  // First: set all non-array fields with merge
  await setDoc(progressionRef, firestoreData, { merge: true });

  // Second: use arrayUnion for each array field to preserve server-side additions
  const arrayUpdates: Record<string, any> = {};
  for (const field of arrayFields) {
    const arr = state[field];
    if (Array.isArray(arr) && arr.length > 0) {
      arrayUpdates[field] = arrayUnion(...arr);
    }
  }

  if (Object.keys(arrayUpdates).length > 0) {
    await updateDoc(progressionRef, arrayUpdates);
  }

  // Mark as synced in Dexie
  await markSynced('progressionStates', [state.userId]);
  console.log(` Uploaded progression state for user ${userId}`);
}

/**
 * Upload a batch of daily activity entries to Firebase
 * Strips rawPayload before upload to prevent Firestore document bloat
 */
async function uploadDailyActivityBatch(userId: string, entries: DBDailyActivity[]): Promise<void> {
  const batch = writeBatch(firestore);
  const queuedIds: string[] = [];
  const skipped: string[] = [];

  for (const entry of entries) {
    // Validate BEFORE anything that can throw. `new Date(bad).toISOString()`
    // below raises the same "RangeError: Invalid time value" that was killing
    // these batches, and it ran before any guard.
    const date = firstValidDate(entry.date, entry.createdAt);
    const createdAt = firstValidDate(entry.createdAt, entry.date);
    if (!date || !createdAt) {
      skipped.push(entry.id);
      continue;
    }

    // Use YYYY-MM-DD as Firestore doc ID
    const dateStr = date.toISOString().split('T')[0];
    const activityRef = doc(firestore, `users/${userId}/dailyActivity`, dateStr);

    // Strip rawPayload before upload (kept in Dexie only for debugging)
    const { rawPayload, ...uploadData } = entry;

    const firestoreData = sanitizeForFirestore({
      ...uploadData,
      date: Timestamp.fromDate(date),
      createdAt: Timestamp.fromDate(createdAt),
      updatedAt: serverTimestamp(),
      syncedAt: serverTimestamp(),
    });

    batch.set(activityRef, firestoreData, { merge: true });
    queuedIds.push(entry.id);
  }

  if (skipped.length > 0) {
    console.warn(`[sync] ${skipped.length} activity entr(ies) skipped — unusable date: ${skipped.join(', ')}`);
  }
  if (queuedIds.length === 0) return;

  await batch.commit();

  // Mark as synced in Dexie
  await markSynced('dailyActivity', queuedIds);
  console.log(` Uploaded ${queuedIds.length} daily activity entries${skipped.length ? ` (${skipped.length} skipped)` : ''}`);
}

/**
 * Upload a batch of nutrition entries to Firebase
 */
async function uploadNutritionBatch(userId: string, entries: NutritionEntry[]): Promise<void> {
  const batch = writeBatch(firestore);

  const queuedIds: string[] = [];
  const skipped: string[] = [];

  for (const entry of entries) {
    // Same batch-poisoning hazard — validate before converting.
    const createdAt = firstValidDate(entry.createdAt);
    if (!createdAt) {
      skipped.push(entry.id);
      continue;
    }
    const nutritionRef = doc(firestore, `users/${userId}/nutrition`, entry.id);

    const firestoreData = sanitizeForFirestore({
      ...entry,
      createdAt: Timestamp.fromDate(createdAt),
      updatedAt: serverTimestamp(),
      syncedAt: serverTimestamp(),
    });

    batch.set(nutritionRef, firestoreData, { merge: true });
    queuedIds.push(entry.id);
  }

  if (skipped.length > 0) {
    console.warn(`[sync] ${skipped.length} nutrition entr(ies) skipped — unusable date: ${skipped.join(', ')}`);
  }
  if (queuedIds.length === 0) return;

  await batch.commit();

  // Mark as synced in Dexie
  const ids = queuedIds;
  await db.nutritionEntries.where('id').anyOf(ids).modify((entry: NutritionEntry) => {
    entry.syncStatus = 'synced';
    entry.updatedAt = new Date();
  });
  console.log(` Uploaded ${entries.length} nutrition entries`);
}

/**
 * Upload a batch of custom meals to Firebase
 */
async function uploadCustomMealsBatch(userId: string, meals: CustomMeal[]): Promise<void> {
  const batch = writeBatch(firestore);

  const skippedMeals: string[] = [];

  for (const meal of meals) {
    // Same batch-poisoning hazard — validate before converting.
    const createdAt = firstValidDate(meal.createdAt);
    if (!createdAt) {
      skippedMeals.push(meal.id);
      continue;
    }
    const mealRef = doc(firestore, `users/${userId}/customMeals`, meal.id);

    const firestoreData = sanitizeForFirestore({
      ...meal,
      createdAt: Timestamp.fromDate(createdAt),
      updatedAt: serverTimestamp(),
      syncedAt: serverTimestamp(),
    });

    batch.set(mealRef, firestoreData, { merge: true });
  }

  if (skippedMeals.length > 0) {
    console.warn(`[sync] ${skippedMeals.length} custom meal(s) skipped — unusable date: ${skippedMeals.join(', ')}`);
  }
  const ids = meals.map(m => m.id).filter(id => !skippedMeals.includes(id));
  if (ids.length === 0) return;

  await batch.commit();

  // Mark as synced in Dexie — only what was actually uploaded.
  await db.customMeals.where('id').anyOf(ids).modify((meal: CustomMeal) => {
    meal.syncStatus = 'synced';
    meal.updatedAt = new Date();
  });
  console.log(` Uploaded ${ids.length} custom meals${skippedMeals.length ? ` (${skippedMeals.length} skipped)` : ''}`);
}

/**
 * Upload spearhead progress to Firebase
 */
async function uploadSpearheadProgress(userId: string): Promise<void> {
  const entries = await db.spearheadProgress
    .where('syncStatus').anyOf(['local', 'pending'])
    .and(e => e.userId === userId)
    .toArray();

  if (entries.length === 0) return;

  for (const entry of entries) {
    // Team deployment progress goes to deployments/{deploymentId}/progress/{userId}
    // Global progress goes to users/{userId}/spearheadProgress/{operationId}
    const isTeamDeployment = !!(entry as any).deploymentId;
    const docRef = isTeamDeployment
      ? doc(firestore, `deployments/${(entry as any).deploymentId}/progress`, userId)
      : doc(firestore, `users/${userId}/spearheadProgress`, entry.operationId);

    const firestoreData = sanitizeForFirestore({
      ...entry,
      updatedAt: serverTimestamp(),
      syncedAt: serverTimestamp(),
    });
    await setDoc(docRef, firestoreData, { merge: true });

    // Mark as synced
    await db.spearheadProgress.update(entry.id, {
      syncStatus: 'synced' as const,
      updatedAt: new Date(),
    });
  }
  console.log(` Uploaded ${entries.length} spearhead progress entries`);
}

// ============================================================================
// DOWNLOAD: Firebase → Dexie
// ============================================================================

/**
 * Download all changes from Firebase since last sync
 */
export async function downloadFirebaseChanges(
  userId: string,
  options: SyncOptions = {}
): Promise<SyncStats> {
  const stats: SyncStats = {
    uploaded: { sessions: 0, recovery: 0, oneRMs: 0, progressionStates: 0, dailyActivity: 0, nutrition: 0, customMeals: 0 },
    downloaded: { sessions: 0, recovery: 0, oneRMs: 0, progressionStates: 0, dailyActivity: 0, nutrition: 0, customMeals: 0 },
    errors: []
  };

  console.log('~ Starting download from Firebase...');

  // Ensure auth token is fresh before making Firestore queries
  try {
    const { getAuth } = await import('firebase/auth');
    const currentUser = getAuth().currentUser;
    if (currentUser) {
      await currentUser.getIdToken(true); // Force token refresh
    } else {
      console.warn('No authenticated user — skipping download');
      stats.errors.push('No authenticated user');
      return stats;
    }
  } catch (tokenErr) {
    console.warn('Token refresh failed, proceeding anyway:', tokenErr);
  }

  // Get last sync time from Dexie
  let lastSync: Date | undefined;
  try {
    lastSync = await getLastSyncTime(userId);
    console.log(` Last sync: ${lastSync ? lastSync.toISOString() : 'never'}`);
  } catch (err) {
    console.warn('Could not read last sync time:', err);
  }

  // Download each collection independently so one failure doesn't block the rest

  // 1. Sessions
  try {
    const sessions = await downloadSessions(userId, lastSync);
    if (sessions.length > 0) {
      const kept = await filterPreserveLocalPending((id) => db.sessions.get(id), sessions, 'session');
      if (kept.length > 0) await safeBulkPut(db.sessions, kept, 'sessions');
      stats.downloaded.sessions = kept.length;
      console.log(` Downloaded ${kept.length} sessions`);
    }
  } catch (error) {
    const msg = `Download sessions failed: ${error}`;
    console.error(msg);
    stats.errors.push(msg);
  }

  // 2. Recovery
  try {
    const recovery = await downloadRecovery(userId, lastSync);
    if (recovery.length > 0) {
      const kept = await filterPreserveLocalPending((id) => db.recovery.get(id), recovery, 'recovery');
      if (kept.length > 0) await safeBulkPut(db.recovery, kept, 'recovery');
      stats.downloaded.recovery = kept.length;
      console.log(` Downloaded ${kept.length} recovery entries`);
    }
  } catch (error) {
    const msg = `Download recovery failed: ${error}`;
    console.error(msg);
    stats.errors.push(msg);
  }

  // 3. 1RMs
  try {
    const oneRMs = await download1RMs(userId);
    if (oneRMs.length > 0) {
      await safeBulkPut(db.oneRMs, oneRMs, '1RMs');
      stats.downloaded.oneRMs = oneRMs.length;
      console.log(` Downloaded ${oneRMs.length} 1RMs`);
    }
  } catch (error) {
    const msg = `Download 1RMs failed: ${error}`;
    console.error(msg);
    stats.errors.push(msg);
  }

  // 4. Progression state
  try {
    const progressionState = await downloadProgressionState(userId);
    if (progressionState) {
      await db.progressionStates.put(progressionState);
      stats.downloaded.progressionStates = 1;
      console.log(` Downloaded progression state for user ${userId}`);
    }
  } catch (error) {
    const msg = `Download progression failed: ${error}`;
    console.error(msg);
    stats.errors.push(msg);
  }

  // 5. Daily activity
  try {
    const dailyActivity = await downloadDailyActivity(userId, lastSync);
    if (dailyActivity.length > 0) {
      const kept = await filterPreserveLocalPending((id) => db.dailyActivity.get(id), dailyActivity, 'daily activity');
      if (kept.length > 0) await safeBulkPut(db.dailyActivity, kept, 'dailyActivity');
      stats.downloaded.dailyActivity = kept.length;
      console.log(` Downloaded ${kept.length} daily activity entries`);
    }
  } catch (error) {
    const msg = `Download daily activity failed: ${error}`;
    console.error(msg);
    stats.errors.push(msg);
  }

  // 6. Nutrition entries
  try {
    const nutrition = await downloadNutrition(userId, lastSync);
    if (nutrition.length > 0) {
      const kept = await filterPreserveLocalPending((id) => db.nutritionEntries.get(id) as any, nutrition, 'nutrition');
      if (kept.length > 0) {
        await safeBulkPut(db.nutritionEntries, kept, 'nutrition');
        // Notify any open Fuel pages so they re-fetch — without this the
        // fuel page reads Dexie once on mount and never sees newly-pulled
        // entries (the "I logged on Tuesday, can't see them Wednesday"
        // bug when sync finishes after the page renders).
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('opmode-nutrition-synced'));
        }
      }
      stats.downloaded.nutrition = kept.length;
      console.log(` Downloaded ${kept.length} nutrition entries`);
    }
  } catch (error) {
    const msg = `Download nutrition failed: ${error}`;
    console.error(msg);
    stats.errors.push(msg);
  }

  // 7. Custom meals
  try {
    const customMeals = await downloadCustomMeals(userId);
    if (customMeals.length > 0) {
      const kept = await filterPreserveLocalPending((id) => db.customMeals.get(id) as any, customMeals, 'custom meal');
      if (kept.length > 0) await safeBulkPut(db.customMeals, kept, 'customMeals');
      stats.downloaded.customMeals = kept.length;
      console.log(` Downloaded ${kept.length} custom meals`);
    }
  } catch (error) {
    const msg = `Download custom meals failed: ${error}`;
    console.error(msg);
    stats.errors.push(msg);
  }

  // 8. Structured assessments (from assessmentSystem → Firebase)
  try {
    const [{ collection, getDocs, query: fbQuery, orderBy, limit: fbLimit }, { db: fireDb }] = await Promise.all([
      import('firebase/firestore'),
      import('./firebase'),
    ]);
    const assessRef = collection(fireDb, 'users', userId, 'assessments');
    const assessSnap = await getDocs(fbQuery(assessRef, orderBy('date', 'desc'), fbLimit(100)));
    let assessCount = 0;
    for (const docSnap of assessSnap.docs) {
      const data = docSnap.data();
      // Only import structured assessments (have day1/day3/day5 fields)
      if (data.day1 || data.day3 || data.day5) {
        const existing = await db.assessmentSessions.get(docSnap.id);
        if (!existing) {
          await db.assessmentSessions.put({ ...data, id: docSnap.id } as any);
          assessCount++;
        }
      }
    }
    if (assessCount > 0) {
      console.log(` Downloaded ${assessCount} structured assessments`);
    }
  } catch (error) {
    console.warn('Download structured assessments failed (non-critical):', error);
  }

  // 9. Spearhead progress
  try {
    const spearheadRef = collection(firestore, 'users', userId, 'spearheadProgress');
    const spearheadSnap = await getDocs(query(spearheadRef, firestoreLimit(50)));
    let spearheadCount = 0;
    for (const docSnap of spearheadSnap.docs) {
      const data = docSnap.data();
      const id = data.id || `${docSnap.id}-${userId}`;
      const existing = await db.spearheadProgress.get(id);
      // Only overwrite if remote is newer
      const remoteUpdated = data.updatedAt?.toDate?.() || new Date(0);
      const localUpdated = existing?.updatedAt || new Date(0);
      if (!existing || remoteUpdated > localUpdated) {
        await db.spearheadProgress.put({
          ...data,
          id,
          updatedAt: remoteUpdated,
          syncStatus: 'synced',
        } as any);
        spearheadCount++;
      }
    }
    if (spearheadCount > 0) {
      console.log(` Downloaded ${spearheadCount} spearhead progress entries`);
    }
  } catch (error) {
    console.warn('Download spearhead progress failed (non-critical):', error);
  }

  // 10. Team deployment spearhead progress
  try {
    const { getMyDeployments } = await import('./teamDeployments');
    const deployments = await getMyDeployments(userId);
    let teamProgressCount = 0;
    for (const dep of deployments) {
      try {
        const progressRef = doc(firestore, 'deployments', dep.id, 'progress', userId);
        const progressSnap = await getDoc(progressRef);
        if (progressSnap.exists()) {
          const data = progressSnap.data();
          const id = data.id || `${dep.id}-${userId}`;
          const existing = await db.spearheadProgress.get(id);
          const remoteUpdated = data.updatedAt?.toDate?.() || new Date(0);
          const localUpdated = existing?.updatedAt || new Date(0);
          if (!existing || remoteUpdated > localUpdated) {
            await db.spearheadProgress.put({
              ...data,
              id,
              deploymentId: dep.id,
              updatedAt: remoteUpdated,
              syncStatus: 'synced',
            } as any);
            teamProgressCount++;
          }
        }
      } catch (e) {
        console.warn(`Download team deployment progress failed for ${dep.id}:`, e);
      }
    }
    if (teamProgressCount > 0) {
      console.log(` Downloaded ${teamProgressCount} team deployment progress entries`);
    }
  } catch (error) {
    console.warn('Download team deployment progress failed (non-critical):', error);
  }

  // 11. Merge program selection (Step 2)
  try {
    await syncProgramSelection(userId);
  } catch (error) {
    console.warn('Program selection merge during download failed (non-critical):', error);
  }

  // Update last sync time if at least something succeeded
  if (stats.errors.length === 0) {
    try {
      await setLastSyncTime(userId, new Date());
    } catch (err) {
      console.warn('Could not update sync time:', err);
    }
  }

  console.log(' Download complete:', stats.downloaded, stats.errors.length > 0 ? `(${stats.errors.length} errors)` : '');

  return stats;
}

//  REMOVED: syncDexieToLocalStorage - Dexie is now single source of truth

/**
 * Download sessions from Firebase
 *  FIXED: Full download doesn't use updatedAt filter (old sessions don't have it)
 * 
 * @param userId - User ID
 * @param since - Only fetch sessions updated after this date (for incremental sync)
 * @param maxCount - Maximum number of sessions to fetch (default: 500, set to 0 for unlimited)
 */
async function downloadSessions(userId: string, since?: Date, maxCount: number = 500): Promise<DBSession[]> {
  const sessionsRef = collection(firestore, `users/${userId}/sessions`);
  
  let q;
  
  if (since) {
    // Incremental sync: use updatedAt filter with composite index
    // This requires the composite index on (date, updatedAt)
    q = query(
      sessionsRef,
      where('updatedAt', '>', Timestamp.fromDate(since)),
      orderBy('updatedAt', 'desc'),  // Order by same field we're filtering
      firestoreLimit(maxCount > 0 ? maxCount : 500)
    );
  } else {
    // Full download: cap at 500 most recent to avoid unbounded reads
    q = query(sessionsRef, firestoreLimit(500));
  }
  
  const snapshot = await getDocs(q);

  console.log(` Downloaded ${snapshot.docs.length} sessions from Firebase`);
  
  return snapshot.docs.map(doc => {
    const data = doc.data();
    
    //  FIXED: Normalize to userId for Dexie queries
    // Old Firebase sessions have userId, we need to ensure it's set
    const normalizedUserId = data.userId || data.oderId || userId;
    
    return {
      ...data,
      id: doc.id,
      userId: normalizedUserId,  //  Primary field for Dexie queries
      oderId: normalizedUserId,  //  Keep for backwards compat until full migration
      date: toDateSafe(data.date) || new Date(),
      startTime: toDateSafe(data.startTime) || new Date(),
      endTime: toDateSafe(data.endTime),
      createdAt: toDateSafe(data.createdAt) || new Date(),
      updatedAt: toDateSafe(data.updatedAt) || new Date(),
      syncedAt: toDateSafe(data.syncedAt),
      syncStatus: 'synced' as const,
    } as DBSession;
  });
}

/**
 * Download recovery from Firebase
 *  FIXED: Full download doesn't use updatedAt filter (old entries don't have it)
 */
async function downloadRecovery(userId: string, since?: Date, maxCount: number = 100): Promise<DBRecoveryEntry[]> {
  const recoveryRef = collection(firestore, `users/${userId}/recovery`);
  
  let q;
  
  if (since) {
    // Incremental sync: use updatedAt filter
    q = query(
      recoveryRef,
      where('updatedAt', '>', Timestamp.fromDate(since)),
      orderBy('updatedAt', 'desc'),
      firestoreLimit(maxCount > 0 ? maxCount : 100)
    );
  } else {
    // Full download: cap at 200 most recent to avoid unbounded reads
    q = query(recoveryRef, firestoreLimit(200));
  }
  
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      ...data,
      id: doc.id,
      date: toDateSafe(data.date) || new Date(),
      createdAt: toDateSafe(data.createdAt) || new Date(),
      updatedAt: toDateSafe(data.updatedAt) || new Date(),
      syncedAt: toDateSafe(data.syncedAt),
      syncStatus: 'synced' as const,
    } as DBRecoveryEntry;
  });
}

/**
 * Download 1RMs from Firebase
 * Step 4: For each remote entry, compare against local Dexie value and keep the HIGHER weight.
 * Filter out invalid entries (weight <= 0, isNaN, or > 2000).
 */
async function download1RMs(userId: string): Promise<DB1RMEntry[]> {
  const userRef = doc(firestore, 'users', userId);
  const snapshot = await getDoc(userRef);

  if (!snapshot.exists()) return [];

  const data = snapshot.data();
  const oneRMsData = data.oneRMs || {};
  const entries: DB1RMEntry[] = [];
  const invalidEntries: string[] = [];

  for (const [exerciseName, value] of Object.entries(oneRMsData)) {
    const entry = value as any;
    const remoteWeight = typeof entry === 'number' ? entry : (entry.weight || 0);

    // Filter out invalid entries
    if (!remoteWeight || isNaN(remoteWeight) || remoteWeight <= 0 || remoteWeight > 2000) {
      invalidEntries.push(`${exerciseName}: ${remoteWeight}`);
      continue;
    }

    const id = `${userId}-${exerciseName}`;

    // Compare against local Dexie value — keep the HIGHER weight
    let finalWeight = remoteWeight;
    try {
      const localEntry = await db.oneRMs.get(id);
      if (localEntry && localEntry.weight > remoteWeight) {
        // Local is higher — don't regress
        finalWeight = localEntry.weight;
      }
    } catch {
      // If local read fails, use remote value
    }

    entries.push({
      id,
      userId: userId,
      oderId: userId,
      exerciseName,
      weight: finalWeight,
      date: toDateSafe(entry.date) || new Date(),
      source: entry.source || 'calculated',
      confidence: entry.confidence || 0.8,
      syncStatus: 'synced',
      updatedAt: toDateSafe(entry.updatedAt) || new Date(),
      syncedAt: new Date(),
    });
  }

  // Log AND delete invalid entries from Firestore so they don't get
  // re-scanned every sync cycle. Before this, the same 4-5 garbage
  // entries (weight=0, NaN, > 2000) would loop forever — fetched,
  // filtered out, warned, then left in place for the next sync to
  // see again. Now we clean up server-side once we see them, breaking
  // the loop. Idempotent — if the delete fails for any reason, next
  // sync just retries.
  if (invalidEntries.length > 0) {
    console.warn(`[sync] Skipped ${invalidEntries.length} invalid 1RM entries:`, invalidEntries);
    try {
      const deletions: Record<string, any> = {};
      for (const entry of invalidEntries) {
        // entry is "exerciseName: weight" — peel off the name.
        const name = entry.split(':')[0].trim();
        if (name) deletions[`oneRMs.${name}`] = deleteField();
      }
      if (Object.keys(deletions).length > 0) {
        await updateDoc(userRef, deletions);
        console.log(`[sync] Purged ${Object.keys(deletions).length} invalid 1RM entries from Firestore`);
      }
    } catch (e) {
      console.warn('[sync] Invalid 1RM cleanup failed (will retry next sync):', e);
    }
  }

  return entries;
}

/**
 * Download progression state from Firebase
 */
async function downloadProgressionState(userId: string): Promise<any | null> {
  const progressionRef = doc(firestore, `users/${userId}/progression`, 'current');
  const snapshot = await getDoc(progressionRef);

  if (!snapshot.exists()) return null;

  const data = snapshot.data();

  // Helper function to safely convert Firestore Timestamp to Date
  const toDateSafe = (val: any): Date | undefined => {
    if (!val) return undefined;
    if (val.toDate && typeof val.toDate === 'function') return val.toDate();
    if (val instanceof Date) return val;
    if (typeof val === 'string' || typeof val === 'number') return new Date(val);
    return undefined;
  };

  const firebaseState: any = {
    ...data,
    userId,
    updatedAt: toDateSafe(data.updatedAt) || new Date(),
    syncedAt: toDateSafe(data.syncedAt),
    syncStatus: 'synced' as const,
  };

  // Merge array fields from Firestore into local Dexie state (union, not replace)
  // This prevents server-side rewards (e.g. operation emblems) from being lost
  try {
    const localState = await db.progressionStates.get(userId);
    if (localState) {
      const mergeArrayFields = [
        'unlockedEmblems', 'unlockedTitles', 'unlockedCallingCards',
        'unlockedAvatars', 'unlockedArchetypes', 'milestonesUnlocked',
        'operatorScoreMilestonesUnlocked', 'awardedSessionIds',
      ];
      for (const field of mergeArrayFields) {
        const local: string[] = (localState as any)[field] || [];
        const remote: string[] = firebaseState[field] || [];
        const merged = Array.from(new Set(local.concat(remote)));
        firebaseState[field] = merged;
      }
      // Take the higher value for intel (server may have awarded bonus intel)
      firebaseState.lifetimeIntel = Math.max(data.lifetimeIntel ?? 0, localState.lifetimeIntel ?? 0);
      firebaseState.levelIntel = Math.max(data.levelIntel ?? 0, localState.levelIntel ?? 0);
    }
  } catch {
    // Non-critical: if local read fails, just use Firestore data as-is
  }

  return firebaseState;
}

/**
 * Download daily activity from Firebase
 * Incremental (since date) or full download, 90-doc limit
 */
async function downloadDailyActivity(userId: string, since?: Date): Promise<DBDailyActivity[]> {
  const activityRef = collection(firestore, `users/${userId}/dailyActivity`);

  let q;
  if (since) {
    q = query(
      activityRef,
      where('updatedAt', '>', Timestamp.fromDate(since)),
      orderBy('updatedAt', 'desc'),
      firestoreLimit(90)
    );
  } else {
    q = query(activityRef, firestoreLimit(90));
  }

  const snapshot = await getDocs(q);

  const toDateSafe = (val: any): Date | undefined => {
    if (!val) return undefined;
    if (val.toDate && typeof val.toDate === 'function') return val.toDate();
    if (val instanceof Date) return val;
    if (typeof val === 'string' || typeof val === 'number') return new Date(val);
    return undefined;
  };

  return snapshot.docs.map(docSnap => {
    const data = docSnap.data();
    const dateStr = docSnap.id; // YYYY-MM-DD

    return {
      ...data,
      id: `${userId}-${dateStr}`,
      userId,
      date: toDateSafe(data.date) || new Date(dateStr),
      createdAt: toDateSafe(data.createdAt) || new Date(),
      updatedAt: toDateSafe(data.updatedAt) || new Date(),
      syncStatus: 'synced' as const,
      // Don't download rawPayload from Firebase (it's not uploaded)
    } as DBDailyActivity;
  });
}

/**
 * Download nutrition entries from Firebase
 * Incremental (since date) or full download
 */
async function downloadNutrition(userId: string, since?: Date): Promise<NutritionEntry[]> {
  const nutritionRef = collection(firestore, `users/${userId}/nutrition`);

  let q;
  if (since) {
    q = query(
      nutritionRef,
      where('updatedAt', '>', Timestamp.fromDate(since)),
      orderBy('updatedAt', 'desc'),
      firestoreLimit(200)
    );
  } else {
    // First/full download on a fresh device. Order by updatedAt desc so
    // the most-recent entries land first — without the orderBy, Firestore
    // returns documents in document-ID order, which could miss specific
    // days for users with > 500 lifetime entries.
    q = query(nutritionRef, orderBy('updatedAt', 'desc'), firestoreLimit(500));
  }

  const snapshot = await getDocs(q);

  const toDateSafe = (val: any): Date => {
    if (!val) return new Date();
    if (val.toDate && typeof val.toDate === 'function') return val.toDate();
    if (val instanceof Date) return val;
    if (typeof val === 'string' || typeof val === 'number') return new Date(val);
    return new Date();
  };

  return snapshot.docs.map(docSnap => {
    const data = docSnap.data();
    return {
      ...data,
      id: docSnap.id,
      userId,
      createdAt: toDateSafe(data.createdAt),
      updatedAt: toDateSafe(data.updatedAt),
      syncStatus: 'synced' as const,
    } as NutritionEntry;
  });
}

/**
 * Download custom meals from Firebase
 */
async function downloadCustomMeals(userId: string): Promise<CustomMeal[]> {
  const mealsRef = collection(firestore, `users/${userId}/customMeals`);
  const q = query(mealsRef, firestoreLimit(200));
  const snapshot = await getDocs(q);

  const toDateSafe = (val: any): Date => {
    if (!val) return new Date();
    if (val.toDate && typeof val.toDate === 'function') return val.toDate();
    if (val instanceof Date) return val;
    if (typeof val === 'string' || typeof val === 'number') return new Date(val);
    return new Date();
  };

  return snapshot.docs.map(docSnap => {
    const data = docSnap.data();
    return {
      ...data,
      id: docSnap.id,
      userId,
      createdAt: toDateSafe(data.createdAt),
      updatedAt: toDateSafe(data.updatedAt),
      syncStatus: 'synced' as const,
    } as CustomMeal;
  });
}

// ============================================================================
// BIDIRECTIONAL SYNC
// ============================================================================

/**
 * Full bidirectional sync (upload then download)
 */
export async function syncAll(
  userId: string,
  options: SyncOptions = {}
): Promise<SyncStats> {
  console.log('� Starting full bidirectional sync...');

  // Before anything else: make sure the load engine can actually see who this
  // user is. Cheap, idempotent, and every prescription downstream depends on it.
  await syncTrainingBaseline(userId);

  const uploadStats = await uploadPendingChanges(userId, options);
  const downloadStats = await downloadFirebaseChanges(userId, options);

  const combinedStats: SyncStats = {
    uploaded: uploadStats.uploaded,
    downloaded: downloadStats.downloaded,
    errors: [...uploadStats.errors, ...downloadStats.errors],
  };

  const total =
    combinedStats.uploaded.sessions + combinedStats.uploaded.recovery + combinedStats.uploaded.oneRMs + combinedStats.uploaded.progressionStates +
    combinedStats.downloaded.sessions + combinedStats.downloaded.recovery + combinedStats.downloaded.oneRMs + combinedStats.downloaded.progressionStates;

  // Invalidate in-memory caches so subsequent reads get fresh Dexie data.
  // The intelligence engine caches itself for 5 minutes — without this
  // invalidation, /metrics opened *before* the first sync completes will
  // keep returning the 0-session "no signal" engine even after sessions
  // land in Dexie.
  if (total > 0) {
    try {
      const { invalidateSessionCache } = await import('./sessionStorage');
      invalidateSessionCache();
    } catch (_) { /* session cache not critical */ }

    if (combinedStats.downloaded.sessions > 0 || combinedStats.uploaded.sessions > 0) {
      try {
        const { invalidateIntelligenceCache } = await import('./unifiedIntelligence');
        invalidateIntelligenceCache();
      } catch (_) { /* engine cache not critical */ }
    }

    console.log(' Sync complete:', {
      uploaded: combinedStats.uploaded,
      downloaded: combinedStats.downloaded,
    });
  } else {
    console.log(' Sync complete: No changes');
  }

  return combinedStats;
}

/**
 * Background sync with retry logic
 */
export async function backgroundSync(
  userId: string,
  options: SyncOptions = {}
): Promise<void> {
  const maxRetries = 3;
  let attempt = 0;

  while (attempt < maxRetries) {
    try {
      await syncAll(userId, options);
      return; // Success
    } catch (error: any) {
      attempt++;
      console.error(` Sync attempt ${attempt} failed:`, error);

      // Handle auth/permission errors by refreshing the token before retry
      if (
        error?.code === 'permission-denied' ||
        error?.code === 'unauthenticated' ||
        error?.message?.includes('401') ||
        error?.message?.includes('Missing or insufficient permissions')
      ) {
        try {
          const { getAuth } = await import('firebase/auth');
          const auth = getAuth();
          if (auth.currentUser) {
            await auth.currentUser.getIdToken(true); // Force refresh
            console.log('Auth token refreshed, will retry sync');
          } else {
            console.warn('No authenticated user — cannot refresh token');
          }
        } catch (tokenErr) {
          console.warn('Auth token refresh failed:', tokenErr);
        }
      }

      if (attempt < maxRetries) {
        // Exponential backoff: 2^attempt seconds
        const delay = Math.pow(2, attempt) * 1000;
        console.log(`Retrying in ${delay / 1000}s...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      } else {
        console.error(' Max retries reached. Sync failed.');
        throw error;
      }
    }
  }
}

// ============================================================================
// SYNC METADATA HELPERS
// ============================================================================

/**
 * Get last sync time for a user
 */
async function getLastSyncTime(userId: string): Promise<Date | undefined> {
  const profile = await db.userProfiles.get(userId);
  return profile?.syncedAt;
}

/**
 * Set last sync time for a user
 *  CRITICAL: Must preserve existing programSelection data!
 */
async function setLastSyncTime(userId: string, time: Date): Promise<void> {
  // Step 5: Use update() to only touch sync fields, avoiding race condition
  // where put() could wipe programSelection or other fields
  const updated = await db.userProfiles.update(userId, {
    syncedAt: time,
    updatedAt: time,
  });

  if (!updated) {
    // Record doesn't exist yet — create it with put()
    await db.userProfiles.put({
      userId,
      syncedAt: time,
      updatedAt: time,
    } as any);
  }
}

/**
 * Get sync status for user (for UI)
 */
export async function getSyncStatus(userId: string): Promise<{
  lastSync?: Date;
  pendingChanges: number;
  status: 'synced' | 'pending' | 'error';
}> {
  const lastSync = await getLastSyncTime(userId);
  
  const [sessions, recovery, oneRMs, dailyActivity, nutrition, customMeals] = await Promise.all([
    db.sessions.where('syncStatus').anyOf(['local', 'pending']).count(),
    db.recovery.where('syncStatus').anyOf(['local', 'pending']).count(),
    db.oneRMs.where('syncStatus').anyOf(['local', 'pending']).count(),
    db.dailyActivity.where('syncStatus').anyOf(['local', 'pending']).count(),
    db.nutritionEntries.where('syncStatus').anyOf(['local', 'pending']).count(),
    db.customMeals.where('syncStatus').anyOf(['local', 'pending']).count(),
  ]);

  const pendingChanges = sessions + recovery + oneRMs + dailyActivity + nutrition + customMeals;

  return {
    lastSync,
    pendingChanges,
    status: pendingChanges > 0 ? 'pending' : 'synced',
  };
}

/**
 * Force sync now (for manual button)
 */
export async function forceSyncNow(userId: string): Promise<SyncStats> {
  console.log('� Force sync requested by user');
  return syncAll(userId, { force: true });
}

/**
 * Force FULL download from Firebase (ignores last sync time)
 * Use this to recover ALL historical sessions from Firebase
 */
export async function forceFullDownloadFromFirebase(userId: string): Promise<{
  sessions: number;
  recovery: number;
  oneRMs: number;
  dailyActivity: number;
  nutrition: number;
  customMeals: number;
}> {
  console.log('~ Starting FULL download from Firebase (ignoring sync timestamps)...');

  const stats = { sessions: 0, recovery: 0, oneRMs: 0, dailyActivity: 0, nutrition: 0, customMeals: 0 };
  const errors: string[] = [];

  // Ensure auth token is fresh
  try {
    const { getAuth } = await import('firebase/auth');
    const currentUser = getAuth().currentUser;
    if (currentUser) {
      await currentUser.getIdToken(true);
    } else {
      console.warn('No authenticated user — skipping full download');
      return stats;
    }
  } catch (tokenErr) {
    console.warn('Token refresh failed, proceeding anyway:', tokenErr);
  }

  // Download each collection independently

  try {
    const sessions = await downloadSessions(userId);
    if (sessions.length > 0) {
      const kept = await filterPreserveLocalPending((id) => db.sessions.get(id), sessions, 'session');
      if (kept.length > 0) await safeBulkPut(db.sessions, kept, 'sessions (full)');
      stats.sessions = kept.length;
      console.log(` Downloaded ${kept.length} sessions from Firebase`);
    }
  } catch (error) {
    errors.push(`sessions: ${error}`);
    console.error('Full download sessions failed:', error);
  }

  try {
    const recovery = await downloadRecovery(userId);
    if (recovery.length > 0) {
      const kept = await filterPreserveLocalPending((id) => db.recovery.get(id), recovery, 'recovery');
      if (kept.length > 0) await safeBulkPut(db.recovery, kept, 'recovery (full)');
      stats.recovery = kept.length;
      console.log(` Downloaded ${kept.length} recovery entries from Firebase`);
    }
  } catch (error) {
    errors.push(`recovery: ${error}`);
    console.error('Full download recovery failed:', error);
  }

  try {
    const oneRMs = await download1RMs(userId);
    if (oneRMs.length > 0) {
      await safeBulkPut(db.oneRMs, oneRMs, '1RMs (full)');
      stats.oneRMs = oneRMs.length;
      console.log(` Downloaded ${oneRMs.length} 1RMs from Firebase`);
    }
  } catch (error) {
    errors.push(`1RMs: ${error}`);
    console.error('Full download 1RMs failed:', error);
  }

  try {
    const dailyActivity = await downloadDailyActivity(userId);
    if (dailyActivity.length > 0) {
      const kept = await filterPreserveLocalPending((id) => db.dailyActivity.get(id), dailyActivity, 'daily activity');
      if (kept.length > 0) await safeBulkPut(db.dailyActivity, kept, 'dailyActivity (full)');
      stats.dailyActivity = kept.length;
      console.log(` Downloaded ${kept.length} daily activity entries from Firebase`);
    }
  } catch (error) {
    errors.push(`dailyActivity: ${error}`);
    console.error('Full download daily activity failed:', error);
  }

  try {
    const nutrition = await downloadNutrition(userId);
    if (nutrition.length > 0) {
      const kept = await filterPreserveLocalPending((id) => db.nutritionEntries.get(id) as any, nutrition, 'nutrition');
      if (kept.length > 0) {
        await safeBulkPut(db.nutritionEntries, kept, 'nutrition (full)');
        // Notify Fuel page so it re-fetches after the full sync lands.
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('opmode-nutrition-synced'));
        }
      }
      stats.nutrition = kept.length;
      console.log(` Downloaded ${kept.length} nutrition entries from Firebase`);
    }
  } catch (error) {
    errors.push(`nutrition: ${error}`);
    console.error('Full download nutrition failed:', error);
  }

  try {
    const customMeals = await downloadCustomMeals(userId);
    if (customMeals.length > 0) {
      const kept = await filterPreserveLocalPending((id) => db.customMeals.get(id) as any, customMeals, 'custom meal');
      if (kept.length > 0) await safeBulkPut(db.customMeals, kept, 'customMeals (full)');
      stats.customMeals = kept.length;
      console.log(` Downloaded ${kept.length} custom meals from Firebase`);
    }
  } catch (error) {
    errors.push(`customMeals: ${error}`);
    console.error('Full download custom meals failed:', error);
  }

  // Merge program selection (Step 2 — full download path)
  try {
    await syncProgramSelection(userId);
  } catch (error) {
    console.warn('Program selection merge during full download failed (non-critical):', error);
  }

  // Update sync timestamp if at least something succeeded
  if (errors.length === 0) {
    try {
      await setLastSyncTime(userId, new Date());
    } catch (err) {
      console.warn('Could not update sync time:', err);
    }
  }

  // Invalidate in-memory session cache so subsequent reads get fresh Dexie data
  try {
    const { invalidateSessionCache } = await import('./sessionStorage');
    invalidateSessionCache();
  } catch (_) { /* session cache not critical */ }

  console.log(' Full download complete:', stats, errors.length > 0 ? `(${errors.length} errors)` : '');

  if (errors.length > 0 && stats.sessions === 0 && stats.recovery === 0 && stats.oneRMs === 0 && stats.dailyActivity === 0 && stats.nutrition === 0 && stats.customMeals === 0) {
    // Everything failed — throw so callers know
    throw new Error(`Full download failed: ${errors.join('; ')}`);
  }

  return stats;
}

// ============================================================================
// UTILITIES
// ============================================================================

/**
 * Split array into chunks
 */
function chunkArray<T>(array: T[], chunkSize: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
}

/**
 * Schedule periodic background sync (every 5 minutes when online)
 */
export function startPeriodicSync(userId: string): () => void {
  console.log('� Starting periodic background sync (5min intervals)');

  const intervalId = setInterval(async () => {
    if (navigator.onLine) {
      try {
        await backgroundSync(userId, { batchSize: 50 });
      } catch (error) {
        console.error('Background sync failed:', error);
      }
    } else {
      console.log('� Offline - skipping sync');
    }
  }, 5 * 60 * 1000); // 5 minutes

  // Return cleanup function
  return () => {
    console.log('� Stopping periodic sync');
    clearInterval(intervalId);
  };
}

